import { Dispatch, ReactNode, SetStateAction, useMemo, useState } from 'react'
import { UserPreferenceNamespace, VisibleFieldData } from '~/api/userPreferences'
import { isClient } from '~/common/nextJsUtils'
import { ignoreProviderMissingError, assertNotInReactContext } from '~/common/reactUtils'
import { StateTuple } from '~/common/types'
import { useDisclosure } from '~/design_system'
import { useSessionStorage } from '~/hooks/useBrowserStorage'
import { useStringUrlState } from '~/hooks/useUrlState'
import { objectHookToContext } from '~/common/hookToContext'
import { DisplayMode } from './ActionBar.DisplayModeButtons'

type FieldStateProps<S> = { value: S; onChange: Dispatch<SetStateAction<S>> }
function stateTupleToObj<S>(s: Readonly<StateTuple<S>>): FieldStateProps<S> {
  return { value: s[0], onChange: s[1] }
}

export type ActionBarConfig = {
  defaultFiltersOpen?: boolean
  defaultVisibleFieldsModalOpen?: boolean
  defaultSearch?: string
  defaultView?: string
  defaultOrdering?: string
  defaultDisplayMode?: DisplayMode
  urlReadWriteEnabled?: boolean
  defaultUserPreferenceNamespace?: UserPreferenceNamespace
  defaultVisibleFields?: VisibleFieldData[]
  searchPlaceholder?: string | React.ReactNode
  // Used to configure the views in the ActionBar
  views?: ReadonlyArray<{ value: string; label: string | React.ReactNode; dataTestId?: string }>
  // Used to configure the sortBy in the ActionBar
  orderings?: ReadonlyArray<{ value: string; label: string | React.ReactNode }>
  // Used to configure the display mode buttons to be shown in the ActionBar
  hasDisplayModeButtons?: boolean
  // Used to configure the visible fields button to be shown in the ActionBar
  hasVisibleFieldsButton?: boolean
}

export type ActionBarApi = ReturnType<typeof useActionBar>

/**
 * @deprecated This hook is meant to be private. Please use ActionBarProvider instead.
 */
export const useActionBar = ({
  defaultFiltersOpen = false,
  defaultVisibleFieldsModalOpen = false,
  defaultSearch,
  defaultView,
  defaultOrdering,
  defaultDisplayMode = 'list',
  urlReadWriteEnabled = true,
  defaultUserPreferenceNamespace = UserPreferenceNamespace.JOB,
  defaultVisibleFields = [],
  ...props
}: ActionBarConfig = {}) => {
  const path = isClient() ? window.location.pathname : 'server'
  const filtersDisclosure = useDisclosure({ defaultIsOpen: defaultFiltersOpen })
  const visibleFieldsDisclosure = useDisclosure({ defaultIsOpen: defaultVisibleFieldsModalOpen })
  const search = stateTupleToObj(useStringUrlState('search', defaultSearch, urlReadWriteEnabled))
  /**
   * @deprecated This has been renamed, use the `view` property instead.
   */
  const filterTab = stateTupleToObj(useStringUrlState('view', defaultView, urlReadWriteEnabled))
  const view = filterTab
  const ordering = stateTupleToObj(useStringUrlState('ordering', defaultOrdering, urlReadWriteEnabled))
  const displayMode = stateTupleToObj(
    useSessionStorage(`action-bar-display-mode ${path}`, defaultDisplayMode) as StateTuple<DisplayMode>
  )
  const isGridDisplayMode = displayMode.value === 'grid'
  const isListDisplayMode = displayMode.value === 'list'
  const userPreferenceNamespace = defaultUserPreferenceNamespace
  const visibleFields = stateTupleToObj(useState(defaultVisibleFields) as StateTuple<VisibleFieldData[]>)

  const queryValues = useMemo(
    () => ({
      search: search?.value,
      view: view?.value,
      ordering: ordering?.value
    }),
    [search?.value, view?.value, ordering?.value]
  )

  return {
    filtersDisclosure,
    visibleFieldsDisclosure,
    search,
    filterTab,
    view,
    ordering,
    displayMode,
    isListDisplayMode,
    isGridDisplayMode,
    userPreferenceNamespace,
    visibleFields,
    queryValues,
    ...props
  }
}

const [_ActionBarProvider, _useActionBarContext] = objectHookToContext(({ config }: { config?: ActionBarConfig }) =>
  // it's ok that we use the deprecated hook here, as this is the only place it SHOULD be used!
  useActionBar(config)
)

export const ActionBarProvider = _ActionBarProvider
export const useActionBarContext = (...args: Parameters<typeof _useActionBarContext>) => {
  const [result] = ignoreProviderMissingError(() => _useActionBarContext(...args))
  return result ?? ({} as unknown as ReturnType<typeof useActionBar> | Record<string, never>)
}

export function useActionBarQueryValues() {
  return useActionBarContext().queryValues
}

function buildActionBarDef<const TConfig extends ActionBarConfig>(config?: TConfig) {
  return {
    // ListLayout passes this to the shared ActionBarProvider, so a config that changes between renders
    // updates the provider's props instead of swapping its component type (which would remount the list).
    config,
    // For callers that render the provider directly, e.g. specs. Not stable across resolveConfig calls.
    Provider: (props: { children: ReactNode }) => <ActionBarProvider config={config} {...props} />,
    useValues: () => useActionBarQueryValues(),
    useContext: () => useActionBarContext()
  }
}

/**
 * @agent-preferred
 * @agent-doc vndly/assets/js/components/v2/ResultsLayout/docs/ActionBar.mdx
 * @agent-gotcha MUST be called outside the React component.
 * @agent-gotcha Configures search/sort/views/displayMode for ListLayout. Pass to ListLayout via actionBarDef prop.
 */
export function defineActionBar<const TConfig extends ActionBarConfig>(config?: TConfig) {
  assertNotInReactContext()
  return buildActionBarDef(config)
}

/**
 * @agent-gotcha MUST be called outside the React component (module level).
 * @agent-gotcha Use over defineActionBar when options depend on runtime data (feature flags, user role).
 *   Call resolveConfig(ctx) inside the component and pass the result to ListLayout via actionBarDef.
 * @agent-gotcha Initial-state options (defaultOrdering, defaultSearch, ...) are only read on first render, so
 *   don't make them depend on context that loads later.
 */
export function defineActionBarWithContext<TContext>(configCallback: (context: TContext) => ActionBarConfig) {
  assertNotInReactContext()
  return {
    resolveConfig: (context: TContext) => buildActionBarDef(configCallback(context)),
    // Static, like defineFiltersWithContext, so defineListDataWithContext can infer query param types.
    useValues: () => useActionBarQueryValues(),
    useContext: () => useActionBarContext()
  }
}
